// usePortalData
// -------------
// The three list/count loaders extracted out of portal.jsx's App component so
// the role-based read filtering can be unit-tested in isolation. Same direction
// as useRequestPersistence: a state-bag factory that receives the App's setters
// and returns the loaders; the App keeps the call sites.
//
// IMPORTANT: these loaders go through the src/data repositories, which read the
// live client via getTypedClient() rather than a captured prop. loadRequests is
// invoked *synchronously inside the session-restore effect* (right after setUser),
// before React re-renders, so a render-time-captured client would still be null at
// that moment. The repos always resolve the client portal.jsx set on the same
// effect tick.
//
// TypeScript source -- usePortalData.js is a thin re-export stub.

import type { Dispatch, SetStateAction } from "react";
import { type AppRequest, listRequestsByShelterId, listVisibleRequests } from "../data/requests";
import {
  countPickerTasks,
  countResponderTasks,
  countTopUpApprovalTasks,
} from "../data/requestTasks";
import { sameRows } from "../utils/sameRows";

interface LoadUser {
  role?: string | null;
  sessionMode?: string | null;
  shelterCode?: string | null;
  shelterUuid?: string | null;
  id?: string | null;
}

export interface PortalDataDeps {
  setRequests: Dispatch<SetStateAction<AppRequest[]>>;
  setUnactionedTaskCount: (n: number) => void;
  setPickerTaskCount: (n: number) => void;
  setTopUpTaskCount: (n: number) => void;
}

// In-flight dedup for loadRequests (architecture-review 3.6). The poll, the OSD
// realtime channel, and post-mutation refreshes all call loadRequests; when they
// overlap we don't want redundant concurrent full-list fetches. These flags are
// MODULE-LEVEL (not useRef / not a closure var) on purpose: usePortalData is a
// plain factory re-invoked every App render (a closure var would reset) and is
// called directly in tests (no React context for useRef). There is only ever one
// App, so a module singleton is safe — same rationale as the client singleton.
// COALESCING, not skipping: a call that arrives while a fetch is running sets a
// "run again" flag, so the LATEST request always gets one trailing fetch — a
// post-mutation refresh is never dropped, it's just deduped with the in-flight one.
let _reqLoadInFlight = false;
let _reqLoadPending = false;
let _reqLoadPendingUser: LoadUser | null = null;

// In-flight coalescing dedup for the three count loaders (findings 2026-08-21 §T2 — they
// had none, so the new 30s poll refresh + a nav could fire redundant concurrent count
// queries). Same shape/rationale as the loadRequests dedup above: a call arriving while
// one is running sets a pending flag so the LATEST state still gets one trailing refresh,
// rather than being dropped. Keyed per counter (responder / picker / topup).
const _countInFlight: Record<string, boolean> = {};
const _countPending: Record<string, boolean> = {};
async function coalesceCount(key: string, run: () => Promise<void>): Promise<void> {
  if (_countInFlight[key]) {
    _countPending[key] = true;
    return;
  }
  _countInFlight[key] = true;
  try {
    do {
      _countPending[key] = false;
      await run();
    } while (_countPending[key]);
  } finally {
    _countInFlight[key] = false;
  }
}

export function usePortalData(deps: PortalDataDeps) {
  const { setRequests, setUnactionedTaskCount, setPickerTaskCount, setTopUpTaskCount } = deps;

  async function loadUnactionedTasks() {
    // Only count responder tasks (picker-only types excluded — owned by the repo).
    await coalesceCount("responder", async () => {
      const count = await countResponderTasks();
      if (count !== null) setUnactionedTaskCount(count);
    });
  }

  async function loadPickerTasks() {
    await coalesceCount("picker", async () => {
      const count = await countPickerTasks();
      if (count !== null) setPickerTaskCount(count);
    });
  }

  async function loadTopUpTasks() {
    await coalesceCount("topup", async () => {
      const count = await countTopUpApprovalTasks();
      if (count !== null) setTopUpTaskCount(count);
    });
  }

  // One full-list read, scoped by role. ONLY a `shelter`-role user is scoped to
  // their own shelter's requests (filtered by shelter_id — the active shelter's
  // surrogate id; identity Phase 2). Everyone else - staff, admin, picker, and
  // combined (internal responder+picker staff) - gets the RLS-scoped "all visible"
  // read. A shelter user without a resolved shelterUuid also falls back to the
  // RLS-scoped read. Table/column/mapping details live in src/data/requests.ts.
  async function fetchRequestsOnce(u: LoadUser) {
    const isShelterScope = u.role === "shelter";
    const reqs =
      isShelterScope && u.shelterUuid
        ? await listRequestsByShelterId(u.shelterUuid)
        : await listVisibleRequests();
    // null = no client yet / load error (already logged in the repo) -> leave state.
    // Compare-before-setState (A1, live-lists overhaul): this is the widest-blast-radius
    // loader — All Requests + the shared requests context. An unchanged poll keeps the
    // same array reference (functional updater returns `prev`), so React bails the
    // re-render and the memoised context values downstream don't bust every 30s.
    if (reqs) setRequests((prev) => (sameRows(prev, reqs) ? prev : reqs));
  }

  // Public loader — deduped. If a load is already running, record that another is
  // wanted (with the latest user) and return; the running loop will do one more
  // pass for it. Otherwise run the loop until no further request has queued up.
  async function loadRequests(u: LoadUser) {
    _reqLoadPendingUser = u;
    if (_reqLoadInFlight) {
      _reqLoadPending = true;
      return;
    }
    _reqLoadInFlight = true;
    try {
      do {
        _reqLoadPending = false;
        const uu = _reqLoadPendingUser;
        if (uu) await fetchRequestsOnce(uu);
      } while (_reqLoadPending);
    } finally {
      _reqLoadInFlight = false;
    }
  }

  return { loadRequests, loadUnactionedTasks, loadPickerTasks, loadTopUpTasks };
}
